import { RootState } from "redux/store";
import { Modal, Form, Dropdown } from "react-bootstrap";
import { useEffect, useRef, useState } from "react";
import { useStockFilter } from "hooks";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { getStockTable, updateStockField, clearFilters,cleanStockDetails, addAGGridSelectedRows, addAGGrideDeselectedRows, getStockTableOptimised } from "redux/slices/Stocks";
import { TimedMessage } from "components/TimedMessage";
import { AgGridReact, CustomCellRendererProps } from "ag-grid-react";
import { ManagedStockOptimizedView } from "models/dbModels/managedStock/ManagedStock";
import { IServerSideSelectionState } from "ag-grid-enterprise";
import { IServerSideDatasource, RowSelectedEvent } from "ag-grid-community";
import { AgGridToolbar, AgGridToolbarRef } from "components/AgGrid/AgGridToolbar";
import { Stock } from "apis/StoreFunctionsApi";
import { createBooleanSelectFilter, createSingleSelectFilter } from "components/AgGrid/FilterComponents/Filters";
import { ManagedSupplier } from "models/dbModels/managedSuppliers/ManagedSupplier";
import { Z } from "models/common/components";
import { ScheduleCodes } from "constants/constants";
import { ManagedDepartment } from "models/dbModels/managedDepartments/ManagedDepartment";
import { ManagedSubdepartment } from "models/dbModels/managedSubdepartments/ManagedSubdepartment";
import { ManagedManufacturer } from "models/dbModels/managedManufacturers/ManagedManufacturer";
import { ManagedStockGroup } from "models/dbModels/managedStockGroups/ManagedStockGroup";
import { getDepartments } from "redux/slices/Departments";
import { getSubDepartments } from "redux/slices/SubDepartment";
import { getSuppliers } from "redux/slices/Suppliers";
import { getStockGroups } from "redux/slices/StockGroups";
import { getManufacturers } from "redux/slices/ManagedManufacturers";
import { ZIcon } from "components/ZIcon";
import { ZIconType } from "helpers/utility/iconMap";

type ManagedStocksPopupProps = {
	show: boolean;
	onClose: () => void;
	onSave: () => void;
};

export function ManagedStocksPopup(props: ManagedStocksPopupProps) {
	const dispatch = useAppDispatch();
	const [saving, setSaving] = useState(false);
	const getStockFilter = useStockFilter();
	const stocks = useAppSelector((state: RootState) => state.stocks);
	const toolbarRef = useRef<AgGridToolbarRef>(null);
	const managedStockGridRef = useRef<AgGridReact>(null);
	const [serverSideSelectionState, setServerSideSelectionState] = useState<{ selectAll: boolean; toggledNodes: Stock[] }>({ selectAll: false, toggledNodes: [] });
	const suppliers = useAppSelector((state: RootState) => state.suppliers);
	const departments = useAppSelector((state: RootState) => state.department);
	const subDepartments = useAppSelector((state: RootState) => state.subDepartment);
	const stockGroups = useAppSelector((state: RootState) => state.stockGroups);
	const manufacturers = useAppSelector((state: RootState) => state.managedManufacturers);
	const [selectedDepartment, setSelectedDepartment] = useState<ManagedDepartment | undefined>(undefined);
	const [filterRequiresRefresh, setFilterRequiresRefresh] = useState<boolean>(true);

	useEffect(() => {
		if (props.show) {
			setSaving(false);
			dispatch(clearFilters());
			dispatch(cleanStockDetails());
		}
	}, [props.show]);

	useEffect(()=>{
		if(managedStockGridRef.current != null && managedStockGridRef.current?.api != undefined)
			managedStockGridRef.current?.api.setGridOption("serverSideDatasource", getServerSideDatasource());
	}, [stocks.keyword]);

	useEffect(() => {
		let rows = 0;
		if (stocks.selectAllFilter) {
			let deselectedRows = 0;
			stocks.deselectedRows.forEach((row: any) => deselectedRows = deselectedRows + row.length);
			rows = stocks.totalRows - deselectedRows;
		} else {
			stocks.selectedRows.forEach((row: any) => rows = rows + row.length);
		}

	}, [stocks.selectedRows, stocks.deselectedRows]);

	useEffect(() => {
		columnCheckDeselectAll();
	}, [stocks.pageSize]);

	function onGridReady(){
		managedStockGridRef.current?.api.deselectAll();
		onSearchClick("");
		var datasource = getServerSideDatasource();
        managedStockGridRef.current?.api.setGridOption("serverSideDatasource", datasource);
	}

	/// Does the api call for report data. also handles grouping requests
	const getServerSideDatasource: () => IServerSideDatasource = () => {
		return {
			getRows: async (params) => {
				let response: any = [];
				response = await dispatch(getStockTableOptimised({					
					keyword: stocks.keyword,
					searchPdes: stocks.searchPdes,
					searchBarcodes: stocks.searchBarcodes,
					offset: params === null? null: params.request.startRow!,
					limit: params.request.endRow != undefined && params.request.startRow != undefined ? params.request.endRow - params.request.startRow : 100,

					supplierIds: stocks.supplierIdFilter == undefined? []: [stocks.supplierIdFilter],
					departmentIds: stocks.departmentIdFilter == undefined? []: [stocks.departmentIdFilter],
					subdepartmentIds: stocks.subdepartmentIdFilter == undefined? []: [stocks.subdepartmentIdFilter],
					manufacturerIds: stocks.manufacturerIdFilter == undefined? []: [stocks.manufacturerIdFilter],
					
					stockGroupIds: stocks.stockGroupIdFilter == undefined? []: [stocks.stockGroupIdFilter],
					active: stocks.activeFilter,
					ethical: stocks.ethicalFilter,
					schedule: stocks.scheduleFilter,
					pbs: stocks.pbsFilter,
					
					sortModels: params.request.sortModel,
				}));

				if (response?.totalRows == 0){
					params.api.showNoRowsOverlay();
				} else {
					params.api.hideOverlay();
				}		
			
				params.success({
					rowData: response.data,
					rowCount: response.totalRows,
				});
			}
		}
	}

	function selectedRowsUpdateAgGrid(rows: any[]) {
		let formatedData:{
			positon: number,
			id: number
		} [] = []
		rows.forEach((xe: any) => formatedData.push({ positon: 0, id: xe.id}));
		dispatch(addAGGridSelectedRows([formatedData]));
	}

	function deselectedRowsUpdateAgGrid(rows: any[]) {		
		let formatedData:{
			positon: number,
			id: number
		} [] = []
		rows.forEach(xe => formatedData.push({ positon: 0, id: xe.id}));
		dispatch(addAGGrideDeselectedRows([formatedData]));
	}

	function onSearchClick(keyword: string) {
		managedStockGridRef.current?.api.deselectAll();
		dispatch(cleanStockDetails());
		if(keyword == stocks.keyword){
			dispatch(updateStockField({ key: "keyword", value: keyword }));
			managedStockGridRef.current?.api.setGridOption("serverSideDatasource", getServerSideDatasource());
		}		
		else {
			dispatch(updateStockField({ key: "keyword", value: keyword }));
		}	
		columnCheckDeselectAll();
	}

	function handleChange(option: any, optionValue: any) {
		dispatch(updateStockField({ key: option, value: optionValue }));
	}

	function columnCheckDeselectAll() {
		handleChange("selectAllFilter", false);
		handleChange("deselectedRows", []);
		handleChange("selectedRows", []);
	}

	function columnCheckSelectAll() {
		handleChange("selectAllFilter", true);
		handleChange("deselectedRows", []);
	}

	function saveSelectedRows() {
		setSaving(true);
		props.onSave();
	}

	const CustomSearchButton = () => {
		return (
			<>
			<Dropdown.Item onClick={() => handleChange("searchPdes", !stocks.searchPdes)}>
				<Form.Check type="checkbox" label="PDE" checked={stocks.searchPdes} onChange={() => handleChange("searchPdes", !stocks.searchPdes)} onClick={e => e.stopPropagation()}/>
			</Dropdown.Item>
			<Dropdown.Item onClick={() => handleChange("searchBarcodes", !stocks.searchBarcodes)}>
				<Form.Check type="checkbox" label="APN" checked={stocks.searchBarcodes} onChange={() => handleChange("searchBarcodes", !stocks.searchBarcodes)} onClick={e => e.stopPropagation()}/>
			</Dropdown.Item>
		</>
		);
	}

	return (
		<Modal backdrop={saving ? "static" : true} size="xl" centered show={props.show} onHide={props.onClose}>
			<Modal.Header closeButton={!saving}>
				<Modal.Title>Managed Stocks</Modal.Title>
			</Modal.Header>
			<Modal.Body className={`${saving && "disabled-div"}`}>
            	<TimedMessage message="Please wait while we add all your data..." delay={30} enabled={saving} />
				<AgGridToolbar
					search={true}
					ref={toolbarRef}
					className="pb-2"
					handleSubmit={(searchState, filterState) => {		
						onSearchClick(searchState?.keyword != undefined? searchState.keyword: "");
					}}
					handleFilterOpen={()=>{
						if(filterRequiresRefresh==true || departments.table.length == 0)
							dispatch(getDepartments());

						if(filterRequiresRefresh==true ||subDepartments.table.length == 0)
							dispatch(getSubDepartments(undefined));

						if(filterRequiresRefresh==true ||suppliers.table.length == 0)
							dispatch(getSuppliers());

						if(filterRequiresRefresh==true ||stockGroups.table.length == 0)
							dispatch(getStockGroups());
						
						if(filterRequiresRefresh==true ||manufacturers.table.length == 0)
							dispatch(getManufacturers());

						setFilterRequiresRefresh(false);
					}}
					handleClearFilters={()=>{
						managedStockGridRef.current?.api.deselectAll();
						dispatch(clearFilters());
					}}
					filters={[
						createSingleSelectFilter<ManagedSupplier>({
							label: "Supplier",
							filterName: "supplierIds",
							options: suppliers.table,
							value: (option) => [option.id],
							display: (option) => option.name,
							hasNoneOption: true,
							onFilterChange: function (e: ManagedSupplier | undefined): void {
								dispatch(updateStockField({ key: "supplierIdFilter", value: e?.id }));
							}
						}),
						createSingleSelectFilter<ManagedDepartment>({
							label: "Department",
							filterName: "departmentIds",
							options: departments.table,
							value: (option) => [stocks.departmentIdFilter],
							display: (option) => option.name,
							onFilterChange: (e) => {
								setSelectedDepartment(e);
								dispatch(updateStockField({ key: "departmentIdFilter", value: e?.id }));
							},
							hasNoneOption: true,
						}),
						createSingleSelectFilter<ManagedSubdepartment>({
							label: "Sub Department",
							filterName: "subdepartmentIds",
							options: subDepartments.table.filter( i=> i.departmentId === selectedDepartment?.id),
							value: (option) => [option.id],
							display: (option) => option.name,
							hasNoneOption: true,
							onFilterChange: (e) => {						
								dispatch(updateStockField({ key: "subdepartmentIdFilter", value: e?.id }));
							},
						}),
						createSingleSelectFilter<ManagedManufacturer>({
							label: "Manufacturer",
							filterName: "manufacturerIds",
							options: manufacturers.table,
							value: (option) => [option.id],
							display: (option) => option.name,
							hasNoneOption: true,
							onFilterChange: (e) => {						
								dispatch(updateStockField({ key: "manufacturerIdFilter", value: e?.id }));
							},
						}),
						createSingleSelectFilter<ManagedStockGroup>({
							label: "Stock Group",
							filterName: "stockGroupIds",
							options: stockGroups.table,
							value: (option) => [option.id],
							display: (option) => option.name,
							hasNoneOption: true,
							onFilterChange: (e) => {						
								dispatch(updateStockField({ key: "stockGroupIdFilter", value: e?.id }));
							},
						}),
						createBooleanSelectFilter({
							label: "Active",
							filterName: "active",
							hasNoneOption: true,
							onFilterChange: (e) => {						
								dispatch(updateStockField({ key: "activeFilter", value: e }));
							},
						}),				
						createBooleanSelectFilter({
							label: "Ethical",
							filterName: "ethical",
							hasNoneOption: true,
							onFilterChange: function (e: boolean | undefined): void {
								dispatch(updateStockField({ key: "ethicalFilter", value: e }));
							}
						}),
						createSingleSelectFilter<string>({
							label: "Schedule",
							filterName: "schedule",
							options: ScheduleCodes,
							value: (option) => option,
							display: (option) => option,
							hasNoneOption: true,
							onFilterChange: function (e: string | undefined): void {
								dispatch(updateStockField({ key: "scheduleFilter", value: e }));
							}
						}),
						createBooleanSelectFilter({
							label: "PBS",
							filterName: "pbs",
							hasNoneOption: true,
							onFilterChange: function (e: boolean | undefined): void {
								dispatch(updateStockField({ key: "pbsFilter", value: e }));
							} 
						})
					]}
					searchProps={
						{totalRows: stocks.totalRows, placeholder: "by Name" + (stocks.searchPdes == true ?" / PDE":"" ) + (stocks.searchBarcodes == true? " / APN" : ""), customSearch: CustomSearchButton(), hideContains: true}
					}
					children={(<div className="d-inline-flex gap-1">
						{ 
						(managedStockGridRef.current?.api && serverSideSelectionState.toggledNodes.length >= 1 && stocks.selectAllFilter != true) &&									
							<Z.Button variant="light" className="h-100" disabled={true} buttonText={serverSideSelectionState.toggledNodes.length+ " Selected"}/>
						}
						{
						(stocks.selectAllFilter == true && stocks.deselectedRows.length == 0) &&
							<Z.Button variant="light" className="h-100" disabled={true} buttonText={stocks.totalRows + " Selected"}/>									
						}
						{
						(stocks.selectAllFilter == true && stocks.deselectedRows.length > 0) &&
							<Z.Button variant="light" className="h-100" disabled={true} buttonText={stocks.totalRows - stocks.deselectedRows[0].length + " Selected"}/>
						}
						{																							
							<Z.Button className="h-100" variant="primary" buttonText="Add Selected" onClick={saveSelectedRows} disabled={serverSideSelectionState.toggledNodes.length == 0 && stocks.selectAllFilter == false}/>
						}
					</div>)}
				/>
				<AgGridReact<ManagedStockOptimizedView>
						ref={managedStockGridRef}
						columnDefs={[
							{
								headerCheckboxSelection: true,
								checkboxSelection: true,
								width: 45,
								sortable: false,
								filter: false,
								suppressHeaderMenuButton: true,
							},
							{
								field: "name", 
								headerName: "Name",
								initialSort:"asc",
								flex: 1,					 
								cellRenderer: (params: CustomCellRendererProps) => {
									return <><ZIcon icon={ZIconType.System} size="14px" iconClassName={`me-1 ${params.data?.productId != null ? "" : "invisible"}`}/> {params.value}</>;
								}
							}
						]}
						rowModelType="serverSide"
						pagination={true}
						paginationPageSize={20}
						getRowId={(p) => p.data.id.toString()}									
						cacheBlockSize={100}
						onGridReady={onGridReady}
						className="ag-theme-quartz ag-theme-custom w-100"
						containerStyle={{ height: "70dvh" }}
						rowSelection="multiple"
						suppressRowClickSelection={true}
						suppressCellFocus={true}
						onSelectionChanged={(e) => {
							const selection = managedStockGridRef.current?.api.getServerSideSelectionState();
							const nodes = selection?.toggledNodes?.map(t => managedStockGridRef.current?.api.getRowNode(t.toString())?.data);
							if(stocks.selectAllFilter == true){
								const matchingData = stocks.table.filter(x=> nodes?.find(e=>e.id == x.id));											
								deselectedRowsUpdateAgGrid(matchingData);											
							} else {
								//@ts-ignore
								setServerSideSelectionState(selection && nodes ? {selectAll: selection.selectAll, toggledNodes: nodes} : {selectAll: false, toggledNodes: []});
								selectedRowsUpdateAgGrid(nodes!);
							}
						}}
						onRowSelected={(event: RowSelectedEvent<any, any>) => {
							if(event.source != "uiSelectAll" && stocks.selectAllFilter == (event.api.getServerSideSelectionState() as IServerSideSelectionState).selectAll){
								return;
							}											
							if((event.api.getServerSideSelectionState() as IServerSideSelectionState).selectAll){
								columnCheckSelectAll();
							} else {
								columnCheckDeselectAll();
							}
						}}										
					/>

			</Modal.Body>
		</Modal>
	);
}